import { memoize } from "@use/decorators";

import {
  getStandardPageCount,
  getTextContent,
} from "@courses-pm2/api/src/ts/utils";

import {
  CaptionedContentElementData,
  CaptionedContentElementModel,
} from "@courses-pm2/api/src/ts/models/elements/CaptionedContentElementModel";

/**
 * Specification of data needed to instantiate a new AccordionModel.
 */
export type AudioData = {
  [K in
    | "autoPlay"
    | "src"
    | "imageSrc"
    | "displayInternalInfo"
    | "transcript"]: AudioModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class AudioModel extends CaptionedContentElementModel {
  /**
   * Specifies if the audio should begin playback immediately if possible.
   */
  public readonly autoPlay?: boolean;

  /**
   * Contains the id of the audio file as hosted in Gyldendal's MediaLibrary.
   */
  public readonly src: string;

  public readonly imageSrc?: string;

  /**
   * Specifies if internal info (ie. title and description) should be rendered
   * inside the audio player.
   */
  public readonly displayInternalInfo?: boolean;

  public readonly transcript?: string;

  /**
   * Apply data given when a new model is being instantiated.
   */
  constructor(initial: AudioData & CaptionedContentElementData) {
    super(initial);

    this.autoPlay = initial.autoPlay;
    this.src = initial.src;
    this.imageSrc = initial.imageSrc;
    this.displayInternalInfo = initial.displayInternalInfo;
    this.transcript = initial.transcript;
  }

  /**
   * There's no preview images available for audio files.
   */
  public async getPreviewImageUrl(): Promise<undefined> {
    return undefined;
  }

  /**
   * @inheritdoc
   *
   * Due to the nature of the calculation of standard pages, media elements
   * aren't counted on their own. We do however include the caption of the
   * element in the calculations.
   */
  // tslint:disable-next-line: completed-docs
  @memoize()
  public getStandardPageCount(): number {
    return getStandardPageCount(getTextContent(this.getCaptionText()));
  }
}
